import type { ConsentChoices } from './consent-signals';

import { GRANT_ALL_CHOICES, UNSET_ALL_CHOICES } from './consent-signals';

/**
 * EU member states plus the EEA (IS, LI, NO), the UK, and Switzerland.
 * Unknown or unresolvable regions are treated as this set — the legally
 * strict direction — rather than as "outside". `getCountryFromIp` returns
 * `undefined` for private ranges, bogons, and unresolvable IPs; defaulting
 * those to implied consent would grant tracking the visitor never agreed to.
 */
export const STRICT_CONSENT_REGION_CODES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'CH',
] as const;

const STRICT_CONSENT_REGION_CODE_SET: ReadonlySet<string> = new Set(
  STRICT_CONSENT_REGION_CODES,
);

export function isStrictConsentRegion(regionCode: string | undefined): boolean {
  if (!regionCode) return true;
  return STRICT_CONSENT_REGION_CODE_SET.has(regionCode.toUpperCase());
}

/**
 * Whether the visitor's T&C acceptance stands in for the answers they have not
 * given: signed in, and outside the strict-consent regions. Anonymous traffic
 * has accepted nothing, and a strict region needs a real answer, so both are
 * asked.
 *
 * A visitor this is true of, and who has recorded nothing at all, has no
 * unanswered question left for a banner to collect — which is why the prompt
 * policy takes it.
 */
export function hasImpliedConsent({
  isSignedIn,
  regionCode,
}: {
  isSignedIn: boolean;
  regionCode: string | undefined;
}): boolean {
  return isSignedIn && !isStrictConsentRegion(regionCode);
}

/**
 * Policy table for the categories a visitor has not answered.
 *
 * Implied grants exist only where {@link hasImpliedConsent} holds, and there
 * they cover every optional category: the T&C acceptance they rest on is not
 * narrower for remembering a language preference than it is for advertising.
 * `essential` is not a choice and never appears here.
 *
 * The result is an overlay for the current pageview, not a decision: an
 * explicit stored answer outranks it, and it is never written to the cookie or
 * the profile.
 */
export function decideConsentDefaults({
  isSignedIn,
  regionCode,
}: {
  isSignedIn: boolean;
  regionCode: string | undefined;
}): ConsentChoices {
  if (!hasImpliedConsent({ isSignedIn, regionCode })) {
    return { ...UNSET_ALL_CHOICES };
  }
  return { ...GRANT_ALL_CHOICES };
}
